// @ts-ignore
import ProfileImg from "@assets/images/profilepic.png";
import { Checkbox } from "@components/ui/checkbox";
import React, { useContext, useEffect, useImperativeHandle, useState } from "react";
import { MeetingContext } from "../CreateMeetingPage";
import { ICompanyId } from "@endpoints/types/candidate.type";
import { ITeamMember } from "@type/hiringStaff.type";
import { useSelector } from "react-redux";
import { RootState } from "@store/reducers";
import HiringTeamMembers from "../components/HiringTeamMembers";
import { HiringCompanyType } from "@utils/constants";

const AttendeeSection = React.forwardRef((_, ref) => {
  const userDetails = useSelector((state: RootState) => state.auth?.user);
  const { meetingRequirements, formData } = useContext(MeetingContext);
  const [selectedRecruiter, setSelectedRecruiter] = useState<ICompanyId>();
  const [selectedHiringMembers, setSelectedHiringMembers] = useState<
    ITeamMember[]
  >([]);
  const [sendPollSwitch, setSendPollSwitch] = useState(false);
  const roleLabelMap: Record<number, string> = {
    [HiringCompanyType.SUPER_ADMIN]: "Super Admin",
    [HiringCompanyType.ADMIN]: "Admin",
    [HiringCompanyType.MANAGER]: "Hiring Manager",
    [HiringCompanyType.TEAM_MEMBER]: "Hiring Team Member",
  };

  const recruiterCompany =
    meetingRequirements?.job?.createdBy?.companyId ||
    formData?.jobDetails?.recruiterAgency;

  useEffect(() => {
    if (recruiterCompany?._id && !selectedRecruiter?._id) {
      setSelectedRecruiter(recruiterCompany);
    }
  }, [recruiterCompany?._id, selectedRecruiter?._id, recruiterCompany]);

  function handleSelectOrDeselectMember(memberDetail: ITeamMember) {
    const isMemberExist = selectedHiringMembers?.find(
      (item) => item?._id === memberDetail?._id
    );
    if (isMemberExist) {
      const hiringTeamMembers = selectedHiringMembers?.filter(
        (item) => item?._id !== memberDetail?._id
      );
      setSelectedHiringMembers(hiringTeamMembers);
    } else {
      setSelectedHiringMembers((prev) => [...prev, memberDetail]);
    }
  }

  useImperativeHandle(
    ref,
    () => ({
      submit: () => {
        return {
          selectedHiringMembers,
          isShareToOnlyHiringTeamMember: sendPollSwitch,
        };
      },
    }),
    [selectedHiringMembers, sendPollSwitch]
  );

  const candidateName =
    meetingRequirements?.candidate?.firstName && meetingRequirements?.candidate?.lastName
      ? `${meetingRequirements?.candidate?.firstName} ${meetingRequirements?.candidate?.lastName}`
      : formData?.candidateDetails?.fullName || "";

  const candidateEmail =
    meetingRequirements?.candidate?.email || formData?.candidateDetails?.email || "";
  const candidateRefNo =
    meetingRequirements?.candidate?.refNo || formData?.candidateDetails?.refNo || "";
  const candidateProfilePic =
    meetingRequirements?.candidate?.profilePic || formData?.candidateDetails?.profilePic || "";

  return (
    <div className="flex gap-10 mt-8 w-full">
      <div className="flex flex-col flex-1 gap-8">
        <div className="flex flex-col gap-2">
          <span className="font-semibold text-xs text-primary">
            Select Recruitment agent to attend meeting
          </span>
          <div className="flex items-center gap-4 border py-2 px-5 rounded-md">
            <Checkbox
              checked={
                !!selectedRecruiter?._id &&
                !!recruiterCompany?._id &&
                selectedRecruiter?._id === recruiterCompany?._id
              }
              onClick={() => {
                if (!recruiterCompany?._id) return;
                const isSameId = selectedRecruiter?._id === recruiterCompany?._id;
                if (isSameId) {
                  setSelectedRecruiter(undefined);
                } else {
                  setSelectedRecruiter(recruiterCompany);
                }
              }}
              disabled={!recruiterCompany?._id}
              className="w-5 h-5"
            />
            <div className="flex items-center gap-3.5">
              {recruiterCompany?.logo ? (
                <img
                  src={recruiterCompany?.logo}
                  className="w-10 h-10 rounded-md border"
                />
              ) : (
                <div className="flex justify-center items-center w-10 h-10 rounded-full bg-gray-200 font-medium text-primary">
                  {recruiterCompany?.name?.charAt(0) || "-"}
                </div>
              )}
              <div className="flex flex-col">
                <span className="text-xs font-medium text-primary">
                  {recruiterCompany?.name || "Recruiter details unavailable"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <HiringTeamMembers
          jobRequirementTeamMembers={meetingRequirements?.jobRequirementTeamMembers}
          selectedHiringMembers={selectedHiringMembers}
          handleSelectOrDeselectMember={handleSelectOrDeselectMember}
          sendPollSwitch={sendPollSwitch}
          setSendPollSwitch={setSendPollSwitch}
        />
      </div>

      <div className="flex flex-col items-center p-3 flex-1 border rounded-md gap-2">
        <span className="font-semibold text-sm">Meeting Attendee</span>
        <div className="flex flex-col w-full bg-light-white-700 rounded-md p-3">
          <span className="font-semibold text-xs text-primary">
            Organiser details:
          </span>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-3.5">
              {userDetails?.profilePic ? (
                <img
                  src={userDetails?.profilePic}
                  className="w-10 h-10 rounded-md"
                />
              ) : (
                <div className="flex justify-center items-center w-10 h-10 rounded-full bg-gray-200 font-medium text-primary">
                  {userDetails?.companyName?.charAt(0)}
                </div>
              )}
              <span className="text-xs font-medium text-primary">
                {userDetails?.companyName}
              </span>
            </div>
            <span className="text-xs text-light-black">
              {userDetails?.refNo}
            </span>
          </div>
          {selectedHiringMembers?.length ? (
            <div className="flex flex-col border-t mt-3.5 pt-3.5 gap-3.5">
              <span className="font-semibold text-xs text-primary">
                Hiring team members
              </span>
              <div className="flex flex-col gap-2">
                {selectedHiringMembers?.map((member) => {
                  return (
                    <div key={member?._id} className="flex items-center gap-3.5">
                      <img src={ProfileImg} className="w-6 h-6 rounded-md" />
                      <div className="flex flex-col">
                        <span className="text-xs font-medium text-light-black">
                          {member?.firstName} {member?.lastName}
                        </span>
                        <span className="text-xs text-gray-500">
                          {roleLabelMap[Number(member?.hiringCompanyType)] || "Member"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col w-full bg-light-white-700 rounded-md p-3">
          <span className="font-semibold text-xs text-primary">
            Candidate details:
          </span>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-3.5">
              {candidateProfilePic ? (
                <img src={candidateProfilePic} className="w-10 h-10 rounded-md" />
              ) : (
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 font-semibold text-primary">
                  {candidateName?.charAt(0) || "-"}
                </div>
              )}
              <div className="flex flex-col">
                <span className="text-xs font-medium text-primary">
                  {candidateName}
                </span>
                <span className="text-xs text-primary">{candidateEmail}</span>
              </div>
            </div>
            <span className="text-xs text-light-black">{candidateRefNo}</span>
          </div>
        </div>

        {selectedRecruiter ? (
          <div className="flex flex-col w-full bg-light-white-700 rounded-md p-3">
            <span className="font-semibold text-xs text-primary">
              Recruitment agent details:
            </span>
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-3.5">
                {selectedRecruiter?.logo ? (
                  <img
                    src={selectedRecruiter?.logo}
                    className="w-10 h-10 rounded-md"
                  />
                ) : (
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 font-semibold text-primary">
                    {selectedRecruiter?.name?.charAt(0)}
                  </div>
                )}
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-primary">
                    {selectedRecruiter?.name}
                  </span>
                </div>
              </div>
              <span className="text-xs text-light-black">
                {selectedRecruiter?.refNo}
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
});

export default AttendeeSection;
